import { useState, useEffect, useCallback } from 'react';
import { collection, getDocs } from 'firebase/firestore';
import { db } from '../firebase/config';
import { registerComposedMechanic } from '../config/composedMechanicRegistry';

/**
 * Loads user homebrew content from Firestore, registers composed mechanics
 * in the global registry, and returns homebrew domain cards for merging
 * into allData.
 *
 * @param {string} userId
 * @returns {{ homebrewCards: object, loaded: boolean, reload: function }}
 */
export default function useHomebrewLoader(userId) {
  const [homebrewCards, setHomebrewCards] = useState({});
  const [loaded, setLoaded] = useState(false);

  const loadAndRegister = useCallback(async () => {
    if (!userId) {
      setLoaded(true);
      return;
    }

    try {
      const snap = await getDocs(
        collection(db, 'users', userId, 'homebrewContent'),
      );

      const cards = {};

      snap.forEach((doc) => {
        const item = doc.data();
        // Support both new format (data) and legacy format (composedMechanic)
        const mechanic = item.data || item.composedMechanic;
        if (item.status !== 'ready' || !mechanic) return;

        // Use stored registryKey if available, otherwise fall back to doc ID
        const key = item.registryKey || doc.id;
        registerComposedMechanic(key, mechanic);

        // If domain card, build an allData-compatible entry for merging
        if (mechanic.meta?.containerContext === 'domain-card') {
          cards[key] = {
            id: key,
            name: mechanic.meta.name || item.meta?.name || 'Homebrew Card',
            type: mechanic.meta.cardType || 'Ability',
            domain: mechanic.meta.domain || 'homebrew',
            level: mechanic.meta.level || 1,
            description: mechanic.meta.summary || '',
            homebrew: true,
          };
        }
      });

      setHomebrewCards(cards);
      setLoaded(true);
    } catch (err) {
      console.error('useHomebrewLoader: failed to load homebrew content', err);
      setLoaded(true);
    }
  }, [userId]);

  useEffect(() => {
    loadAndRegister();
  }, [loadAndRegister]);

  return { homebrewCards, loaded, reload: loadAndRegister };
}
